import React from 'react';

import { Box, Stack, Typography } from '@mui/material';

import FunnelStageLabel from './FunnelStageLabel';
import PositionPill from './PositionPill';
import { UI, cardSx, funnelWidths, stageDefinitions, stepDefinitions, value } from './operationalUi';

export interface FunnelMetric {
  value: number | null;
  median: number | null;
  positionQuartile?: string;
  n: number;
  reason?: string;
}
interface Props {
  metrics: Record<string, FunnelMetric>;
  level: string;
  market: string;
  weakestStep: string | null;
  tooEarly: boolean;
  daysObserved: number;
  zeroSale: boolean;
}
export default function FunnelChart({ metrics, level, market, weakestStep, tooEarly, daysObserved, zeroSale }: Props) {
  const pill = (id: string) => {
    const m = metrics[id];
    return (
      <PositionPill
        compact
        position={m?.positionQuartile}
        level={level}
        n={m?.n}
        reason={m?.reason}
        label={
          market && ['impressions_per_day', 'impressions_to_views'].includes(id)
            ? 'Not available by market'
            : tooEarly
              ? `${daysObserved} observed days, too early`
              : zeroSale && ['packages_per_day', 'margin_per_day'].includes(id)
                ? 'No sales recorded'
                : undefined
        }
      />
    );
  };
  return (
    <Box
      sx={{
        ...cardSx,
        p: 1.5,
        height: '100%',
        minHeight: 0,
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 0.35,
        '@container (max-width:600px)': { minHeight: 760, overflow: 'visible' },
      }}
    >
      <Stack direction="row" spacing={1} alignItems="baseline" sx={{ flexShrink: 0, flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: UI.teal, letterSpacing: 0.6 }}>
          FUNNEL V PEERS
        </Typography>
        <Typography sx={{ fontSize: 12, color: UI.muted }}>This campaign v peer median</Typography>
      </Stack>
      {stageDefinitions.map((stage, i) => {
        const m = metrics[stage.id];
        const actual = m?.value,
          median = m?.median,
          slot = [200, 158, 114, 72][i],
          geometry = funnelWidths(actual, median, slot);
        return (
          <React.Fragment key={stage.id}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '140px minmax(0,1fr) 100px',
                gap: 1,
                alignItems: 'center',
                flex: 1,
                minHeight: 80,
                flexShrink: 0,
                '@container (max-width:600px)': {
                  gridTemplateColumns: 'minmax(0,1fr) 110px',
                  gap: 0.75,
                  '& > :first-child': { gridColumn: '1 / -1' },
                },
              }}
            >
              <FunnelStageLabel step={i + 1} label={stage.label}>
                <Typography sx={{ fontSize: 12, color: UI.muted }}>{stage.basis}</Typography>
              </FunnelStageLabel>
              <Stack spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
                <svg
                  data-testid="flash-campaign-funnel-stage"
                  viewBox="0 0 200 28"
                  width="100%"
                  height="26"
                  role="img"
                  aria-label={`${stage.label} ${value(actual, stage.unit)}, peer median ${value(median, stage.unit)}`}
                >
                  <rect
                    x={(200 - slot) / 2}
                    y="1"
                    width={geometry.medianWidth ?? 0}
                    height="26"
                    fill={UI.in_line.background}
                    stroke={UI.teal}
                    strokeOpacity=".5"
                    rx="2"
                  />
                  {geometry.actualWidth !== null && (
                    <rect x={(200 - slot) / 2} y="5" width={geometry.actualWidth} height="18" fill={UI.teal} rx="1" />
                  )}
                </svg>
                {pill(stage.id)}
              </Stack>
              <Box>
                <Typography sx={{ fontSize: 17, fontWeight: 600 }}>{value(actual, stage.unit)}</Typography>
                <Typography sx={{ fontSize: 12, color: UI.muted }}>peer median {value(median, stage.unit)}</Typography>
              </Box>
            </Box>
            {i < 3 && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  py: 0.25,
                  flexShrink: 0,
                  borderTop: `1px solid ${UI.line}`,
                }}
              >
                <Typography sx={{ fontSize: 12, color: UI.muted }}>
                  {weakestStep === stepDefinitions[i].id && (
                    <Box component="span" sx={{ fontWeight: 600, color: UI.teal, mr: 0.6 }}>
                      Weakest step
                    </Box>
                  )}
                  {stepDefinitions[i].label}{' '}
                  <Box component="span" sx={{ color: UI.ink, fontWeight: 600 }}>
                    {value(metrics[stepDefinitions[i].id]?.value, stepDefinitions[i].unit)}
                  </Box>{' '}
                  · peers {value(metrics[stepDefinitions[i].id]?.median, stepDefinitions[i].unit)}
                </Typography>
                {pill(stepDefinitions[i].id)}
              </Box>
            )}
          </React.Fragment>
        );
      })}
      <Typography sx={{ fontSize: 12, color: UI.muted, pt: 0.5, flexShrink: 0, lineHeight: 1.3 }}>
        Solid: this campaign. Outlined: peer median. Widths compare within a stage.
        {(metrics.impressions_to_views?.value || 0) > 1 &&
          ' Views exceed impressions because these are separate event measures, not a session conversion.'}
      </Typography>
    </Box>
  );
}
